import { useState, useEffect, useCallback } from 'react';
import { 
  filterStateManager, 
  SalesFilterState, 
  initializeFilterStateManager,
  getSalesFilterState,
  updateSalesFilterState,
  clearSalesFilterState
} from '../../shared/filterStateManager';

export interface UseSalesFiltersReturn {
  // Filter state
  startDate: Date | null;
  endDate: Date | null;
  brand: string[];
  skus: string[];
  channel: string;
  
  // Filter actions
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  setBrand: (brand: string[]) => void;
  setSkus: (skus: string[]) => void;
  setChannel: (channel: string) => void;
  
  // Utility actions
  clearFilters: () => void;
  hasActiveFilters: boolean;
  isLoading: boolean;
}

/**
 * Custom hook for managing Sales Dashboard filter state with persistence
 */
export const useSalesFilters = (): UseSalesFiltersReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [filterState, setFilterState] = useState<SalesFilterState>({
    startDate: null,
    endDate: null,
    brand: [],
    skus: [],
    channel: '',
  });

  // Initialize filter state manager and load saved state
  useEffect(() => {
    const initializeAndLoad = async () => {
      try {
        setIsLoading(true);
        
        // Initialize the filter state manager if not already done
        if (!filterStateManager.isInitialized()) {
          await initializeFilterStateManager();
        }
        
        // Load the saved filter state
        const savedState = getSalesFilterState();
        setFilterState(savedState);
        
        console.log('🔄 Sales filters loaded from storage:', savedState);
      } catch (error) {
        console.error('❌ Error loading sales filter state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAndLoad();
  }, []);

  // Update individual filter values
  const setStartDate = useCallback(async (date: Date | null) => {
    setFilterState(prevState => {
      const newState = { ...prevState, startDate: date };
      updateSalesFilterState({ startDate: date });
      return newState;
    });
  }, []);

  const setEndDate = useCallback(async (date: Date | null) => {
    setFilterState(prevState => {
      const newState = { ...prevState, endDate: date };
      updateSalesFilterState({ endDate: date });
      return newState;
    });
  }, []);

  const setBrand = useCallback(async (brand: string[]) => {
    setFilterState(prevState => {
      const newState = { ...prevState, brand };
      updateSalesFilterState({ brand });
      return newState;
    });
  }, []);

  const setSkus = useCallback(async (skus: string[]) => {
    setFilterState(prevState => {
      const newState = { ...prevState, skus };
      updateSalesFilterState({ skus });
      return newState;
    });
  }, []);

  const setChannel = useCallback(async (channel: string) => {
    setFilterState(prevState => {
      const newState = { ...prevState, channel };
      updateSalesFilterState({ channel });
      return newState;
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(async () => {
    const clearedState = {
      startDate: null,
      endDate: null,
      brand: [],
      skus: [],
      channel: '',
    };
    setFilterState(clearedState);
    await clearSalesFilterState();
    console.log('🗑️ Sales filters cleared');
  }, []);

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    filterState.startDate || 
    filterState.endDate || 
    filterState.brand.length > 0 || 
    filterState.skus.length > 0 || 
    filterState.channel
  );

  return {
    // Filter state
    startDate: filterState.startDate,
    endDate: filterState.endDate,
    brand: filterState.brand,
    skus: filterState.skus,
    channel: filterState.channel,
    
    // Filter actions
    setStartDate,
    setEndDate,
    setBrand,
    setSkus,
    setChannel,
    
    // Utility actions
    clearFilters,
    hasActiveFilters,
    isLoading,
  };
};
